import deepEqual from 'deep-equal';

// Small, dependency-light replacements for the ramda helpers whose native
// equivalent is not a safe one-liner. Each function mirrors the semantics of
// its ramda counterpart (see fp.test.js for the pinned behavior).

// R.isNil
export const isNil = x => x == null;

// R.isEmpty — true only for empty string / array / plain object (matches ramda,
// which does not treat Map/Set or non-emptyable values as empty).
export const isEmpty = x => {
  if (x == null) return false;
  if (typeof x === 'string' || Array.isArray(x)) return x.length === 0;
  if (typeof x === 'object') {
    const proto = Object.getPrototypeOf(x);
    if (proto === Object.prototype || proto === null) return Object.keys(x).length === 0;
  }
  return false;
};

// R.equals — deep, value-based equality.
export const equals = (a, b) => deepEqual(a, b, { strict: true });

// R.prop
export const prop = (key, obj) => (obj == null ? undefined : obj[key]);

// R.has — own property check (true even when the value is undefined).
export const has = (key, obj) => obj != null && Object.prototype.hasOwnProperty.call(obj, key);

// R.path — nested lookup, undefined as soon as a step is nil. Integer keys use
// nth semantics like ramda, so a negative index counts from the end.
export const path = (keys, obj) =>
  keys.reduce((acc, key) => {
    if (acc == null) return undefined;
    if (typeof key === 'number' && Number.isInteger(key) && key < 0) return acc[acc.length + key];
    return acc[key];
  }, obj);

// R.defaultTo — default on null / undefined / NaN.
const orDefault = (value, fallback) =>
  value == null || (typeof value === 'number' && Number.isNaN(value)) ? fallback : value;

// R.pathOr
export const pathOr = (fallback, keys, obj) => orDefault(path(keys, obj), fallback);

// R.propOr
export const propOr = (fallback, key, obj) => orDefault(prop(key, obj), fallback);

// R.pick — keep listed keys that exist on obj.
export const pick = (keys, obj) => {
  const res = {};
  if (obj == null) return res;
  for (const key of keys) if (key in obj) res[key] = obj[key];
  return res;
};

// R.omit — copy own enumerable keys except the listed ones.
export const omit = (keys, obj) => {
  const drop = new Set(keys);
  const res = {};
  if (obj == null) return res;
  for (const key of Object.keys(obj)) if (!drop.has(key)) res[key] = obj[key];
  return res;
};

// R.groupBy — { key -> [items] }, key produced by fn.
export const groupBy = (fn, list) => {
  const res = {};
  for (const item of list) {
    const key = fn(item);
    if (Object.prototype.hasOwnProperty.call(res, key)) res[key].push(item);
    else res[key] = [item];
  }
  return res;
};

// R.indexBy — { key -> last item }, key produced by fn.
export const indexBy = (fn, list) => {
  const res = {};
  for (const item of list) res[fn(item)] = item;
  return res;
};

// R.map over an object — apply fn to each value, preserving keys.
export const mapValues = (fn, obj) => {
  const res = {};
  for (const key of Object.keys(obj)) res[key] = fn(obj[key]);
  return res;
};

// R.propEq — obj[key] deep-equals value. Curried: propEq(v, k)(obj) or propEq(v, k, obj).
// Uses arguments.length so propEq(v, k, undefined) still evaluates (to false) rather
// than returning the curried function.
export function propEq(value, key, obj) {
  const check = target => equals(value, prop(key, target));
  return arguments.length < 3 ? check : check(obj);
}

// R.pathEq — value at keys deep-equals value. Curried like propEq.
export function pathEq(value, keys, obj) {
  const check = target => equals(value, path(keys, target));
  return arguments.length < 3 ? check : check(obj);
}

// R.mergeRight — shallow merge, right wins. Curried: mergeRight(l)(r) or mergeRight(l, r).
export function mergeRight(left, right) {
  const merge = r => ({ ...left, ...r });
  return arguments.length < 2 ? merge : merge(right);
}

// R.mergeLeft — shallow merge, left wins.
export const mergeLeft = (left, right) => ({ ...right, ...left });

// R.assoc — shallow set. An array stays an array only for an integer key; a
// string key on an array yields a plain object (ramda's internal _assoc), which
// is what makes assocPath(['facets', 'datasourceId'], …) work on an empty [].
export const assoc = (key, value, obj) => {
  if (Number.isInteger(key) && Array.isArray(obj)) {
    const arr = [...obj];
    arr[key] = value;
    return arr;
  }
  return { ...obj, [key]: value };
};

// R.dissoc — shallow remove.
export const dissoc = (key, obj) => omit([key], obj);

// R.assocPath — set a value at a nested path, cloning along the way.
export const assocPath = (keys, value, obj) => {
  if (keys.length === 0) return value;
  const [key, ...rest] = keys;
  const base = obj == null ? {} : obj;
  return assoc(key, rest.length ? assocPath(rest, value, base[key]) : value, base);
};

// R.dissocPath — remove the value at a nested path.
export const dissocPath = (keys, obj) => {
  if (keys.length === 0) return obj;
  const [key, ...rest] = keys;
  if (rest.length === 0) return dissoc(key, obj);
  if (obj == null || typeof obj[key] === 'undefined') return obj;
  return assoc(key, dissocPath(rest, obj[key]), obj);
};

// R.over(R.lensProp(key), fn) — transform obj[key] with fn. Goes through assoc,
// like the ramda lens does, so the array/object shape rules stay the same.
export const overProp = (key, fn, obj) => assoc(key, fn(prop(key, obj)), obj);

// R.over(R.lensPath(keys), fn) — transform the nested value at keys with fn.
export const overPath = (keys, fn, obj) => assocPath(keys, fn(path(keys, obj)), obj);

// R.pluck — collect one property from each item.
export const pluck = (key, list) => list.map(item => prop(key, item));

// R.defaultTo — default on null / undefined / NaN. Curried: defaultTo(d)(v) or defaultTo(d, v).
export function defaultTo(fallback, value) {
  const apply = v => orDefault(v, fallback);
  return arguments.length < 2 ? apply : apply(value);
}

// R.when(pred, fn) — apply fn when pred is truthy, else identity. Curried or full.
export function when(pred, fn, value) {
  const apply = v => (pred(v) ? fn(v) : v);
  return arguments.length < 3 ? apply : apply(value);
}

// R.unnest — flatten one level.
export const unnest = list => list.flat();

// R.transpose — transpose a 2D list; ragged rows fill by index (ramda semantics).
export const transpose = list => {
  const result = [];
  for (const row of list) {
    for (let j = 0; j < row.length; j += 1) {
      if (result[j] === undefined) result[j] = [];
      result[j].push(row[j]);
    }
  }
  return result;
};

// R.splitAt — [take(index), drop(index)].
export const splitAt = (index, list) => [list.slice(0, index), list.slice(index)];

// R.concat — concat two arrays or strings. Curried: concat(a)(b) or concat(a, b).
export function concat(a, b) {
  const apply = second => a.concat(second);
  return arguments.length < 2 ? apply : apply(b);
}

// R.uniq — dedupe a list, keeping first occurrences, using value-based equality.
export const uniq = list => {
  const result = [];
  for (const item of list) {
    if (!result.some(x => equals(x, item))) result.push(item);
  }
  return result;
};

// R.difference — unique elements of a not present in b (value-based equality).
export const difference = (a, b) => {
  const result = [];
  for (const item of a) {
    if (!b.some(x => equals(x, item)) && !result.some(x => equals(x, item))) result.push(item);
  }
  return result;
};

// R.invertObj — swap keys and values (values become string keys, last wins).
export const invertObj = obj => {
  const res = {};
  for (const key of Object.keys(obj)) res[obj[key]] = key;
  return res;
};

// R.filter — keep matching entries; works on arrays and plain objects like ramda.
export const filter = (pred, functor) => {
  if (Array.isArray(functor)) return functor.filter(pred);
  const res = {};
  for (const key of Object.keys(functor)) if (pred(functor[key])) res[key] = functor[key];
  return res;
};

// R.reject — complement of filter.
export const reject = (pred, functor) => filter(x => !pred(x), functor);

// R.evolve — apply a shape of transformations to the matching keys of obj.
export const evolve = (transformations, obj) => {
  const res = Array.isArray(obj) ? [...obj] : { ...obj };
  for (const key of Object.keys(transformations)) {
    if (!(key in res)) continue;
    const t = transformations[key];
    res[key] = typeof t === 'function' ? t(res[key]) : evolve(t, res[key]);
  }
  return res;
};

// R.mergeDeepRight — recursive merge; right wins on conflicts, plain objects merge.
const isPlainObject = v =>
  v != null && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date);

export const mergeDeepRight = (left, right) => {
  if (!isPlainObject(left) || !isPlainObject(right)) return right;
  const res = { ...left };
  for (const key of Object.keys(right)) {
    res[key] =
      isPlainObject(left[key]) && isPlainObject(right[key])
        ? mergeDeepRight(left[key], right[key])
        : right[key];
  }
  return res;
};
